import { useState } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  IntegrityIndicator,
  AuditTimeline,
  ComplianceStandards,
  ExportCompliance,
  ComplianceReportPDF,
  ElectronicSignature,
  SignatureVerification,
  AuditFilters,
  AuditTable,
  AlertCard,
  AlertStats,
  AlertFilters,
  INDRAShowcase,
  type AuditFiltersState,
  type AlertFiltersState,
} from '@/components/Compliance';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import {
  useComplianceMetrics,
  useAuditLog,
  useComplianceAlerts,
  useVerifyIntegrity,
  useAcknowledgeAlert,
  useResolveAlert,
} from '@/hooks/useCompliance';
import { useAuth } from '@/contexts/AuthContext';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  PenTool,
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Search,
  RefreshCw,
  Download,
  Eye,
  Hash,
  Lock,
  Unlock,
  Bell,
  BellOff,
  Loader2,
  ChevronRight,
  Calendar,
  User,
  Database,
  Link2,
  FileCheck,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import type {
  AuditLogRecord,
  ComplianceAlert,
  ActionCategory,
  SensitivityLevel,
  AlertSeverity,
  AlertStatus,
} from '@/types/compliance';
import {
  actionCategoryLabels,
  sensitivityLabels,
  alertSeverityLabels,
  alertStatusLabels,
  sensitivityColors,
  alertSeverityColors,
  alertStatusColors,
  actionCategoryColors,
} from '@/types/compliance';

// Colores para graficos
const CHART_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

export default function ComplianceDashboard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [period, setPeriod] = useState<number>(30);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAlert, setSelectedAlert] = useState<ComplianceAlert | null>(null);
  const [selectedAuditRecord, setSelectedAuditRecord] = useState<AuditLogRecord | null>(null);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [auditFilters, setAuditFilters] = useState<AuditFiltersState>({
    search: '',
    entityType: 'all',
    actionCategory: 'all',
    sensitivityLevel: 'all',
    dateFrom: undefined,
    dateTo: undefined,
  });
  const [alertFilters, setAlertFilters] = useState<AlertFiltersState>({
    search: '',
    severity: 'all',
    status: 'all',
  });
  const [acknowledgingAlertId, setAcknowledgingAlertId] = useState<string | null>(null);
  const [resolvingAlertId, setResolvingAlertId] = useState<string | null>(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [lastSignature, setLastSignature] = useState<{ id: string; code: string } | null>(null);

  // Queries
  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useComplianceMetrics(period);
  const { data: auditLogs, isLoading: auditLoading } = useAuditLog({
    limit: 100,
    entityType: auditFilters.entityType !== 'all' ? auditFilters.entityType : undefined,
    actionCategory: auditFilters.actionCategory !== 'all' ? auditFilters.actionCategory : undefined,
    sensitivityLevel: auditFilters.sensitivityLevel !== 'all' ? auditFilters.sensitivityLevel : undefined,
    dateFrom: auditFilters.dateFrom?.toISOString(),
    dateTo: auditFilters.dateTo?.toISOString(),
    search: auditFilters.search || undefined,
  });
  const { data: alerts, isLoading: alertsLoading } = useComplianceAlerts({
    limit: 100,
    status: alertFilters.status !== 'all' ? alertFilters.status : undefined,
    severity: alertFilters.severity !== 'all' ? alertFilters.severity : undefined,
  });

  // Filter alerts by search text locally
  const filteredAlerts = alerts?.filter(alert => {
    if (!alertFilters.search) return true;
    const search = alertFilters.search.toLowerCase();
    return (
      alert.title.toLowerCase().includes(search) ||
      alert.description.toLowerCase().includes(search) ||
      (alert.entity_type?.toLowerCase().includes(search)) ||
      (alert.recommendation?.toLowerCase().includes(search))
    );
  });

  // Entity types for filter dropdown
  const entityTypes = [...new Set(auditLogs?.map(log => log.entity_type) || [])];

  // Mutations
  const verifyIntegrity = useVerifyIntegrity();
  const acknowledgeAlert = useAcknowledgeAlert();
  const resolveAlert = useResolveAlert();

  // Preparar datos para graficos
  const categoryChartData = metrics?.audit_metrics.by_category
    ? Object.entries(metrics.audit_metrics.by_category).map(([key, value]) => ({
        name: actionCategoryLabels[key as ActionCategory] || key,
        value: value as number,
      }))
    : [];

  const sensitivityChartData = metrics?.audit_metrics.by_sensitivity
    ? Object.entries(metrics.audit_metrics.by_sensitivity).map(([key, value]) => ({
        name: sensitivityLabels[key as SensitivityLevel] || key,
        value: value as number,
      }))
    : [];

  const handleVerifyIntegrity = () => {
    verifyIntegrity.mutate({});
  };

  const handleAcknowledgeAlert = (alert: ComplianceAlert) => {
    if (!user?.id) return;
    setAcknowledgingAlertId(alert.id);
    acknowledgeAlert.mutate(
      { id: alert.id, userId: user.id },
      { onSettled: () => setAcknowledgingAlertId(null) }
    );
  };

  const handleResolveAlertInline = (alert: ComplianceAlert, notes: string) => {
    if (!user?.id) return;
    setResolvingAlertId(alert.id);
    resolveAlert.mutate(
      { id: alert.id, userId: user.id, resolution_notes: notes },
      { onSettled: () => setResolvingAlertId(null) }
    );
  };

  const handleDismissAlert = (alert: ComplianceAlert) => {
    if (!user?.id) return;
    setResolvingAlertId(alert.id);
    resolveAlert.mutate(
      { id: alert.id, userId: user.id, resolution_notes: 'Marcado como falso positivo', status: 'false_positive' },
      { onSettled: () => setResolvingAlertId(null) }
    );
  };

  const handleResolveAlert = () => {
    if (!selectedAlert || !user?.id || !resolutionNotes.trim()) {
      toast({
        title: 'Error',
        description: 'Debe ingresar notas de resolucion',
        variant: 'destructive',
      });
      return;
    }
    resolveAlert.mutate({
      id: selectedAlert.id,
      userId: user.id,
      resolution_notes: resolutionNotes,
    });
    setAlertDialogOpen(false);
    setResolutionNotes('');
    setSelectedAlert(null);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-7 w-7 text-primary" />
              Centro de Compliance
            </h1>
            <p className="text-muted-foreground">
              Auditoria, alertas e integridad del sistema - ISO 27001 / ENS / CMMI
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period.toString()} onValueChange={(v) => setPeriod(parseInt(v))}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Ultimos 7 dias</SelectItem>
                <SelectItem value="30">Ultimos 30 dias</SelectItem>
                <SelectItem value="90">Ultimos 90 dias</SelectItem>
              </SelectContent>
            </Select>
            <ComplianceReportPDF
              auditLogs={auditLogs}
              alerts={alerts}
              metrics={metrics}
              period={period}
            />
            <ExportCompliance
              auditLogs={auditLogs}
              alerts={alerts}
              metrics={metrics}
              period={period}
            />
            <Button variant="outline" size="icon" onClick={() => refetchMetrics()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Total Auditorias */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Registros Auditoria</p>
                  <p className="text-2xl font-bold">
                    {metrics?.audit_metrics.total_records?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ultimos {period} dias
                  </p>
                </div>
                <Activity className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          {/* Alertas Abiertas */}
          <Card className={cn(
            "border-l-4",
            (metrics?.alert_metrics.open_alerts || 0) > 0 ? "border-l-orange-500" : "border-l-green-500"
          )}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Alertas Abiertas</p>
                  <p className="text-2xl font-bold">
                    {metrics?.alert_metrics.open_alerts || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {metrics?.alert_metrics.critical_open || 0} criticas
                  </p>
                </div>
                {(metrics?.alert_metrics.open_alerts || 0) > 0 ? (
                  <Bell className="h-10 w-10 text-orange-500" />
                ) : (
                  <BellOff className="h-10 w-10 text-green-500" />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Alto Riesgo */}
          <Card className={cn(
            "border-l-4",
            (metrics?.audit_metrics.high_risk_actions || 0) > 0 ? "border-l-red-500" : "border-l-gray-300"
          )}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Acciones Alto Riesgo</p>
                  <p className="text-2xl font-bold">
                    {metrics?.audit_metrics.high_risk_actions || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Sensibilidad alta/critica
                  </p>
                </div>
                <AlertTriangle className="h-10 w-10 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Indicador de Integridad Visual */}
        <IntegrityIndicator
          isValid={metrics?.integrity_status.chain_valid ?? true}
          recordsChecked={metrics?.integrity_status.records_checked ?? 0}
          isLoading={verifyIntegrity.isPending}
          onVerify={handleVerifyIntegrity}
        />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 lg:w-[620px]">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Resumen
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Auditoria
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Alertas
              {(metrics?.alert_metrics.open_alerts || 0) > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {metrics?.alert_metrics.open_alerts}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="signatures" className="flex items-center gap-2">
              <PenTool className="h-4 w-4" />
              Firmas
            </TabsTrigger>
            <TabsTrigger value="standards" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              INDRA
            </TabsTrigger>
          </TabsList>

          {/* Tab: Resumen */}
          <TabsContent value="overview" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Grafico por Categoria */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Acciones por Categoria</CardTitle>
                  <CardDescription>Distribucion de operaciones auditadas</CardDescription>
                </CardHeader>
                <CardContent>
                  {categoryChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={categoryChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No hay datos disponibles
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Grafico por Sensibilidad */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Por Nivel de Sensibilidad</CardTitle>
                  <CardDescription>Clasificacion de riesgo</CardDescription>
                </CardHeader>
                <CardContent>
                  {sensitivityChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={sensitivityChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {sensitivityChartData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      No hay datos disponibles
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Timeline de Actividad Reciente */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Actividad Reciente
                </CardTitle>
                <CardDescription>
                  Ultimas operaciones registradas en el sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {auditLogs && auditLogs.length > 0 ? (
                    <AuditTimeline
                      records={auditLogs}
                      maxItems={15}
                      onSelect={(record) => {
                        setSelectedAuditRecord(record);
                        setAuditDialogOpen(true);
                      }}
                    />
                  ) : (
                    <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground">
                      <Activity className="h-12 w-12 mb-2" />
                      <p>No hay actividad reciente</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Auditoria */}
          <TabsContent value="audit" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Registro de Auditoria Inmutable</CardTitle>
                    <CardDescription>
                      Cadena de hashes SHA-256 - Cada registro enlazado al anterior
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-green-100 text-green-800">
                      <Lock className="h-3 w-3 mr-1" />
                      Hash Chain Activo
                    </Badge>
                    <Badge variant="outline">
                      {auditLogs?.length || 0} registros
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filtros avanzados */}
                <AuditFilters
                  filters={auditFilters}
                  onChange={setAuditFilters}
                  entityTypes={entityTypes}
                />

                {/* Tabla con expansion */}
                <AuditTable
                  records={auditLogs || []}
                  isLoading={auditLoading}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Alertas */}
          <TabsContent value="alerts" className="space-y-4 mt-4">
            {/* Stats Panel */}
            {alerts && alerts.length > 0 && (
              <AlertStats alerts={alerts} />
            )}

            {/* Alerts List */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Centro de Alertas</CardTitle>
                    <CardDescription>
                      Deteccion automatica de anomalias y violaciones de politica
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-sm">
                    {filteredAlerts?.length || 0} de {alerts?.length || 0} alertas
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filtros */}
                <AlertFilters
                  filters={alertFilters}
                  onChange={setAlertFilters}
                />

                <ScrollArea className="h-[550px] pr-4">
                  {filteredAlerts && filteredAlerts.length > 0 ? (
                    <div className="space-y-3">
                      {filteredAlerts.map((alert) => (
                        <AlertCard
                          key={alert.id}
                          alert={alert}
                          onAcknowledge={handleAcknowledgeAlert}
                          onResolve={handleResolveAlertInline}
                          onDismiss={handleDismissAlert}
                          isAcknowledging={acknowledgingAlertId === alert.id}
                          isResolving={resolvingAlertId === alert.id}
                        />
                      ))}
                    </div>
                  ) : alerts && alerts.length > 0 ? (
                    <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground">
                      <Search className="h-12 w-12 mb-4" />
                      <p className="text-lg font-medium">Sin resultados</p>
                      <p className="text-sm">No hay alertas que coincidan con los filtros aplicados</p>
                    </div>
                  ) : (
                    <div className="h-[300px] flex flex-col items-center justify-center text-muted-foreground">
                      <ShieldCheck className="h-16 w-16 mb-4 text-green-500" />
                      <p className="text-lg font-medium">Sin alertas pendientes</p>
                      <p className="text-sm">El sistema esta operando dentro de los parametros normales</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Firmas Electronicas */}
          <TabsContent value="signatures" className="space-y-4 mt-4">
            {/* KPIs de Firmas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Firmas Hoy</p>
                      <p className="text-2xl font-bold">-</p>
                    </div>
                    <PenTool className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Firmas Validas</p>
                      <p className="text-2xl font-bold text-green-600">100%</p>
                    </div>
                    <ShieldCheck className="h-8 w-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Nivel eIDAS</p>
                      <p className="text-lg font-bold">Sustancial</p>
                    </div>
                    <Shield className="h-8 w-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Panel de Prueba de Firma */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PenTool className="h-5 w-5" />
                    Firma Electronica
                  </CardTitle>
                  <CardDescription>
                    Firme documentos de forma segura con validez legal (eIDAS)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-white">eIDAS</Badge>
                      <Badge variant="outline" className="bg-white">ISO 27001</Badge>
                      <Badge variant="outline" className="bg-white">ENS</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Sistema de firma electronica con hash SHA-256, codigo de verificacion
                      unico y registro en cadena de auditoria inmutable.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-muted rounded">
                      <span className="text-muted-foreground">Algoritmo:</span>
                      <span className="ml-1 font-medium">RSA-SHA256</span>
                    </div>
                    <div className="p-2 bg-muted rounded">
                      <span className="text-muted-foreground">Nivel:</span>
                      <span className="ml-1 font-medium">Sustancial</span>
                    </div>
                    <div className="p-2 bg-muted rounded">
                      <span className="text-muted-foreground">Timestamp:</span>
                      <span className="ml-1 font-medium">RFC 3161</span>
                    </div>
                    <div className="p-2 bg-muted rounded">
                      <span className="text-muted-foreground">Verificacion:</span>
                      <span className="ml-1 font-medium">12 chars</span>
                    </div>
                  </div>

                  <Button onClick={() => setSignatureDialogOpen(true)} className="w-full">
                    <PenTool className="h-4 w-4 mr-2" />
                    Probar Firma Electronica
                  </Button>

                  {lastSignature && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Firma registrada</p>
                        <p className="text-xs text-green-600 font-mono">{lastSignature.code}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Panel de Verificacion */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5" />
                    Verificar Firma
                  </CardTitle>
                  <CardDescription>
                    Compruebe la autenticidad de una firma electronica
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <SignatureVerification />

                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">
                      <strong>No-Repudio:</strong> Cada firma incluye identidad del firmante,
                      timestamp certificado y hash criptografico del documento. La revocacion
                      genera una alerta de compliance automatica.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Segregacion de Funciones */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Segregacion de Funciones (CMMI 3)
                </CardTitle>
                <CardDescription>
                  Control de doble firma y separacion de responsabilidades
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 border rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <FileText className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="font-medium">Ordenes de Trabajo</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Quien crea no puede aprobar
                    </p>
                    <Badge variant="outline" className="text-xs">1 aprobador</Badge>
                  </div>

                  <div className="p-4 border rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="font-medium">Control Calidad</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Requiere segunda revision
                    </p>
                    <Badge variant="outline" className="text-xs">1 aprobador</Badge>
                  </div>

                  <div className="p-4 border rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <FileCheck className="h-4 w-4 text-purple-600" />
                      </div>
                      <span className="font-medium">Docs Legales</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Doble firma obligatoria
                    </p>
                    <Badge variant="outline" className="text-xs">2 aprobadores</Badge>
                  </div>

                  <div className="p-4 border rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                        <User className="h-4 w-4 text-orange-600" />
                      </div>
                      <span className="font-medium">Gestion Usuarios</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Cambios de rol supervisados
                    </p>
                    <Badge variant="outline" className="text-xs">superadmin</Badge>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Politica activa:</strong> Las operaciones criticas requieren
                    aprobacion de un usuario diferente al solicitante, cumpliendo con
                    los requisitos de segregacion de funciones de CMMI Nivel 3.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Showcase INDRA */}
          <TabsContent value="standards" className="space-y-4 mt-4">
            <INDRAShowcase
              metrics={metrics}
              onVerifyIntegrity={handleVerifyIntegrity}
              isVerifying={verifyIntegrity.isPending}
            />
          </TabsContent>
        </Tabs>

        {/* Dialog: Detalle de Registro de Auditoria */}
        <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Detalle de Registro de Auditoria
              </DialogTitle>
            </DialogHeader>
            {selectedAuditRecord && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Secuencia</Label>
                    <p className="font-mono">#{selectedAuditRecord.sequence_number}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Timestamp</Label>
                    <p>{format(new Date(selectedAuditRecord.timestamp_utc), 'dd/MM/yyyy HH:mm:ss', { locale: es })}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Entidad</Label>
                    <p className="font-medium">{selectedAuditRecord.entity_type}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">ID Entidad</Label>
                    <p className="font-mono text-xs">{selectedAuditRecord.entity_id}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Accion</Label>
                    <Badge className={cn(actionCategoryColors[selectedAuditRecord.action_category])}>
                      {actionCategoryLabels[selectedAuditRecord.action_category]}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Sensibilidad</Label>
                    <Badge variant="outline" className={cn(sensitivityColors[selectedAuditRecord.sensitivity_level])}>
                      {sensitivityLabels[selectedAuditRecord.sensitivity_level]}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Usuario</Label>
                    <p>{selectedAuditRecord.performed_by_name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Rol</Label>
                    <p>{selectedAuditRecord.performed_by_role}</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-muted-foreground">Integridad Criptografica</Label>
                  <div className="mt-2 space-y-2 bg-muted p-3 rounded-lg font-mono text-xs">
                    <div className="flex items-center gap-2">
                      <Link2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">Hash anterior:</span>
                      <span className="truncate">{selectedAuditRecord.previous_hash}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-green-500" />
                      <span className="text-muted-foreground">Hash registro:</span>
                      <span className="truncate text-green-600">{selectedAuditRecord.record_hash}</span>
                    </div>
                  </div>
                </div>

                {selectedAuditRecord.change_summary && (
                  <div className="border-t pt-4">
                    <Label className="text-muted-foreground">Resumen del Cambio</Label>
                    <p className="mt-1">{selectedAuditRecord.change_summary}</p>
                  </div>
                )}

                {selectedAuditRecord.compliance_tags.length > 0 && (
                  <div className="border-t pt-4">
                    <Label className="text-muted-foreground">Tags de Compliance</Label>
                    <div className="flex gap-2 mt-1">
                      {selectedAuditRecord.compliance_tags.map((tag, i) => (
                        <Badge key={i} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Dialog: Resolver Alerta */}
        <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Resolver Alerta</DialogTitle>
              <DialogDescription>
                Ingrese las notas de resolucion para cerrar esta alerta.
              </DialogDescription>
            </DialogHeader>
            {selectedAlert && (
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="font-medium">{selectedAlert.title}</p>
                  <p className="text-sm text-muted-foreground">{selectedAlert.description}</p>
                </div>
                <div>
                  <Label htmlFor="resolution">Notas de Resolucion *</Label>
                  <Textarea
                    id="resolution"
                    placeholder="Describa como se resolvio esta alerta..."
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    className="mt-1"
                    rows={4}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAlertDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleResolveAlert} disabled={resolveAlert.isPending}>
                {resolveAlert.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Resolver Alerta
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog: Firma Electronica */}
        <ElectronicSignature
          open={signatureDialogOpen}
          onOpenChange={setSignatureDialogOpen}
          documentType="legal_document"
          documentId="00000000-0000-0000-0000-000000000001"
          documentTitle="Documento de Prueba - Compliance"
          documentDescription="Este es un documento de prueba para verificar el sistema de firma electronica"
          onSigned={(id, code) => setLastSignature({ id, code })}
        />
      </div>
    </DashboardLayout>
  );
}
